import { FC, useEffect } from 'react';
import { Form, Button, Col, Row } from 'react-bootstrap';
import FormContainer from '../../../components/FormContainer';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux/hooks';
import { validateEmail } from '../../../shared/validation/email';
import { validatePasswordLength } from '../../../shared/validation/length';
import { login, reset } from '../authSlice';
import { selectedUser } from '../authSlice';
import SmashSpinner from '../../../components/SmashSpinner/SmashSpinner';
import { useForm } from "react-hook-form";
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const LoginFormComponent: FC = () => {
    const dispatch = useAppDispatch();

    type FormValues = {
        username: string;
        password: string;
    };

    const { handleSubmit, setError, formState: { errors }, register } = useForm<FormValues>({
        mode: "onSubmit",
        reValidateMode: "onChange"
    });

    const submitHandler = (values: FormValues) => {
        dispatch(login(values));
    }

    const { isLoading, isError, error, isSuccess, isAuthenticated } = useAppSelector(selectedUser);

    useEffect(() => {
        if (isError) {
            setError("username", { type: "custom", message: (error?.message === 'notCompatible') ? `This account is not compatible with the Smash APi service. try login at  <a class="link-danger" href="https://fromsmash.com/signin" target="_self">fromsmash.com/signin</a>` : "Wrong Email or password" });
            setError("password", { type: "custom" });
        }
        if (isSuccess && isAuthenticated) {
            dispatch(reset());
        }
        if (!isAuthenticated) {
            return;
        }
    }, [isSuccess, isAuthenticated, isError, dispatch]);

    if (isLoading)
        return (
            <div className="h-100 d-flex d-column align-items-center">
                <SmashSpinner />
            </div>
        );

    return (
        <FormContainer>
            <Helmet>
                <title>Smash API - Login</title>
                <meta name="description" content="Login to your Smash API account to access our services." />
            </Helmet>
            <h5 className="my-5"><strong>Sign in to your account</strong></h5>
            <Form noValidate onSubmit={handleSubmit(submitHandler)}>
                <Form.Group className="mb-3" controlId="email">
                    <Form.Label><strong>Email</strong></Form.Label>
                    <Form.Control
                        {...register('username', { required: true, validate: validateEmail })}
                        type="email" placeholder="Enter your email"
                        isInvalid={!!errors.username} />
                    {errors.username?.type === "validate" && (
                        <Form.Control.Feedback type="invalid">
                            Provide a valid email
                        </Form.Control.Feedback>)}
                    {errors.username?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            An email is required
                        </Form.Control.Feedback>)}
                </Form.Group>
                <Form.Group className="mb-3" controlId="password">
                    <Form.Label><strong>Password</strong></Form.Label>
                    <Form.Control
                        {...register('password', { required: true, validate: validatePasswordLength })}
                        type="password" placeholder="Password"
                        isInvalid={!!errors.password}
                    />
                    {errors.password?.type === "validate" && (
                        <Form.Control.Feedback type="invalid">
                            Your password must contain at least 4 char
                        </Form.Control.Feedback>)}
                    {errors.password?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            The password is required
                        </Form.Control.Feedback>)}
                </Form.Group>
                <Row >
                    <Col className="d-flex justify-content-around my-2">
                        {isError && errors.username && errors.username.message && (
                            <div className="text-danger text-center" dangerouslySetInnerHTML={{ __html: errors.username.message }} />)}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="d-grid gap-2">
                            <Button variant="primary" type="submit">
                                Continue
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="my-3 fs-7 d-flex justify-content-center align-items-center">
                            <p>Don't have an account? <Link className="link-primary fw-bolder" to="/signup">Sign up</Link></p>
                        </div>
                    </Col>
                </Row>
            </Form>
        </FormContainer>
    )
}

export default LoginFormComponent;